import { api } from '../lib/api'

export type CreatorEventStatus = 'draft' | 'upcoming' | 'live' | 'past' | 'cancelled' | 'sold_out' | string

export type CreatorEventRow = {
  id: number
  title: string
  event_starts_at: string | null
  event_ends_at: string | null
  event_venue_name?: string
  event_address?: string
  format?: 'event' | string
  is_draft: boolean
  event_is_ticketed: boolean
  event_ticket_price_pixe: number | null
  event_capacity: number | null
  event_is_cancelled: boolean
  event_sales_closed: boolean
  tickets_sold: number
  tickets_remaining: number | null
  gross_pixe: number
  fee_pixe: number
  net_pixe: number
  status: CreatorEventStatus
  created_at: string
  updated_at: string
  location_label?: string
}

export type CreatorEventsResponse = {
  results: CreatorEventRow[]
  metrics: {
    upcoming_events: number
    tickets_sold_30d: number
    tickets_sold_all: number
    gross_pixe: number
    fee_pixe: number
    net_pixe: number
  }
}

export type CreatorEventOrdersResponse = {
  count: number
  page: number
  page_size: number
  aggregates?: {
    paid_qty: number
    paid_gross_pixe: number
    paid_fee_pixe: number
    paid_net_pixe: number
    paid_redeemed_qty?: number
  }
  results: Array<{
    id: number
    buyer_id: number
    buyer_handle: string
    buyer_username: string
    qty: number
    price_pixe_each: number
    total_pixe: number
    fee_pixe: number
    net_pixe: number
    redeemed_qty?: number
    status: string
    created_at: string
    updated_at: string
  }>
}

export type EventCheckinRow = {
  ticket_serial: string
  order_id: number
  buyer_handle: string
  buyer_username: string
  redeemed_at: string
  redeem_source?: string
}

export type EventRecentCheckinsResponse = {
  results: EventCheckinRow[]
}

export type TicketVerifyResponse = {
  valid: boolean
  detail?: string
  ticket_status?: string
  is_redeemed?: boolean
  redeemed_at?: string | null
  redeemed_by?: string
  receipt_status?: string
  event?: {
    id: number
    title: string
    starts_at?: string | null
    venue?: string
  }
}

export async function listCreatorEvents() {
  const { data } = await api.get<CreatorEventsResponse>('/api/creator/events/')
  return data
}

export async function getCreatorEvent(postId: number | string) {
  const { data } = await api.get<CreatorEventRow>(`/api/creator/events/${postId}/`)
  return data
}

export async function listCreatorEventOrders(postId: number | string, params?: { q?: string; status?: string; page?: number; page_size?: number }) {
  const { data } = await api.get<CreatorEventOrdersResponse>(`/api/creator/events/${postId}/orders/`, { params })
  return data
}

export async function listCreatorEventRecentCheckins(postId: number | string) {
  const { data } = await api.get<EventRecentCheckinsResponse>(`/api/creator/events/${postId}/checkins/recent/`)
  return data
}

export async function verifyTicketQrPayload(qrPayload: Record<string, any>) {
  const { data } = await api.post<TicketVerifyResponse>('/api/tickets/verify/', { qr_payload: qrPayload })
  return data
}

export async function verifyEventTicketBySerial(postId: number | string, serial: string) {
  const { data } = await api.get<TicketVerifyResponse>(`/api/events/${postId}/tickets/${serial}/verify/`)
  return data
}

export async function redeemEventTicketBySerial(
  postId: number | string,
  serial: string,
  payload?: { source?: 'manual' | 'qr' | 'scan'; device_id?: string }
) {
  const { data } = await api.post(`/api/events/${postId}/tickets/${serial}/redeem/`, payload || {})
  return data
}

export async function redeemEventTicketFromQr(
  postId: number | string,
  qrPayload: Record<string, any>,
  payload?: { source?: 'manual' | 'qr' | 'scan'; device_id?: string }
) {
  const { data } = await api.post(`/api/events/${postId}/tickets/redeem-from-qr/`, {
    qr_payload: qrPayload,
    ...(payload || {}),
  })
  return data
}

export async function closeCreatorEventSales(postId: number | string) {
  const { data } = await api.post(`/api/creator/events/${postId}/close-sales/`)
  return data
}

export async function openCreatorEventSales(postId: number | string) {
  const { data } = await api.post(`/api/creator/events/${postId}/open-sales/`)
  return data
}

export async function cancelCreatorEvent(postId: number | string) {
  const { data } = await api.post(`/api/creator/events/${postId}/cancel/`)
  return data
}

export function creatorEventExportUrl(
  postId: number | string,
  kind: 'orders' | 'attendees',
  params?: { q?: string; status?: string }
) {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  if (params?.status && params.status !== 'all') sp.set('status', params.status)
  const query = sp.toString()
  return `/api/creator/events/${postId}/${kind}/export.csv${query ? `?${query}` : ''}`
}

